import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TitleText from '../../../../atoms/TitleText';
import InfoText from '../../../../atoms/InfoText';
import Button from '../../../../atoms/Button';
import { closeModal, useModal } from '../../../../../store/slices/Modals';
import { useDispatch } from 'react-redux';
import { addSellerService } from '../../../../../store/services/addSellerService';
import { sellerProfileCreationService } from '../../../../../store/services/sellerProfileCreationService';
import CustomModal from '../../../../atoms/CustomModal';
import YadLogo from '../../../../../assets/icons/YadLogo';
import celebrateGif from '../../../../../assets/images/celebrate.gif';

export default function SuccessMarketplaceModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sellerId } = useParams<{ sellerId: string }>();
  const { modals } = useModal();

  const currentModal = modals['SUCCESS_MARKETPLACE_MODAL'];

  if (!currentModal?.isOpen) return null;

  const handleClose = () => {
    dispatch(closeModal({ id: 'SUCCESS_MARKETPLACE_MODAL' }));
  };

  const handleCheckPortal = () => {
    handleClose();
    dispatch(addSellerService.util.invalidateTags(['Sellers']));
    
    if (!sellerId) {
      navigate('/sellers');
      return;
    }

    dispatch(sellerProfileCreationService.util.invalidateTags(['ProfileInfo']));
    navigate(`/sellers/${sellerId}/profile`, { state: { activeTab: 'overview' } });
  };

  return (
    <CustomModal
      isOpen={currentModal?.isOpen}
      onClose={handleClose}
      title=""
      closable={false}
      width={500}
      centered
    >
      <div
        className="relative flex flex-col items-center justify-center gap-4 text-center p-6 w-full"
        data-testid="marketplace-success-modal-content"
      >
        <div className="mb-2" data-testid="marketplace-success-logo">
          <YadLogo width={64} height={64} />
        </div>

        <div className="relative w-full h-40 flex items-center justify-center" data-testid="marketplace-success-animation-container">
          <div className="w-24 h-24 rounded-full bg-[#F2F9F4] flex items-center justify-center z-10 border-2 border-[#4DA167] shadow-sm">
            <svg
              className="w-12 h-12 text-[#4DA167]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <div className="absolute w-full h-80 z-10 scale-125 pointer-events-none" data-testid="marketplace-success-celebration-image">
          <img
            src={celebrateGif}
            alt="Success"
            className="object-contain w-full h-full"
          />
        </div>

        <div className="space-y-2 z-10 mt-4" data-testid="marketplace-success-text-container">
          <TitleText
            className="text-light-text-primary dark:text-dark-text-primary font-bold text-center w-full mx-auto"
            text={t('createMarket.setupSuccessfully')}
            size="xl"
            data-testid="marketplace-success-title"
          />

          <InfoText
            className="text-text-secondary dark:text-dark-text-secondary font-normal mx-auto mt-2"
            text={t('createMarket.setupSuccessfullyDesc')}
            size="sm"
            maxLength="FULL"
            data-testid="marketplace-success-description"
          />
        </div>

        <Button
          className="w-full mt-6 z-10 !bg-black !text-white !border-black hover:!bg-gray-900 font-semibold py-3 dark:!bg-dark-text-primary dark:!text-dark-surface-primary dark:!border-dark-text-primary"
          onClick={handleCheckPortal}
          data-testid="marketplace-success-btn-check-portal"
        >
          {t('createMarket.checkPortal')}
        </Button>
      </div>
    </CustomModal>
  );
}
